import React from "react";
import { ChangeEvent, Fragment, InputHTMLAttributes, RefObject } from "react";
import ManualSelect from "./ManualSelect";

interface IValidateInputProps extends InputHTMLAttributes<HTMLElement> {
    validator?: RegExp | ((value: string) => boolean);
    invalidMessage?: string;
    filter?: RegExp | ((value: string) => string);
    isRequired?: boolean;
    requiredMessage?: string;
    type?: string;
}

interface IValidateInputState {
    value?: string | number | string[];
    isValid?: boolean;
    isMissing?: boolean;
    hasFocused: boolean;
}

class ValidateInput extends React.Component<IValidateInputProps, IValidateInputState> {
    private innerElement: RefObject<any>;

    constructor(props: Readonly<IValidateInputProps>) {
        super(props);

        this.state = {
            hasFocused: false,
            isMissing: undefined,
            isValid: undefined,
            value: this.runFilter(this.props.value ? this.props.value.toString() : "")
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.runFilter = this.runFilter.bind(this);
        this.runValidation = this.runValidation.bind(this);

        this.innerElement = React.createRef();
    }

    public componentDidUpdate(prevProps: Readonly<IValidateInputProps>) {
        if (prevProps.value !== this.props.value) {
            this.setState({
                value: this.runFilter(this.props.value ? this.props.value.toString() : "")
            });
        }

        if (prevProps.isRequired !== this.props.isRequired && !this.props.isRequired) {
            // Clear required styles
            this.setState({
                isMissing: undefined,
                isValid: undefined,
            });
        }
    }

    public validate(scrollIntoView = false) {
        const result = this.runValidation(this.state.value ? this.state.value.toString() : "", true);
        this.setState({ hasFocused: true });

        if (!result && scrollIntoView) {
            this.scrollIntoView();
        }
        return result;
    }

    public scrollIntoView() {
        if (this.innerElement.current && this.innerElement.current.scrollIntoView) {
            this.innerElement.current.scrollIntoView();
        }
    }

    public render() {
        const {
            onChange, invalidMessage, isRequired, requiredMessage, filter, validator,
            value, className, onFocus, onBlur, ...props
        } = this.props;

        let innerElement;
        switch (this.props.type) {
            case "select":
                innerElement =
                    <select ref={this.innerElement} className={(className !== undefined ? className : "") + ((this.state.isValid === false || this.state.isMissing === true) ? " field-error" : "")} onChange={this.handleChange} onFocus={this.handleFocus} onBlur={this.handleBlur} value={this.state.value} {...props}>
                        {this.props.children}
                    </select>;
                break;
            case "select-other":
                innerElement =
                    <ManualSelect className={(className !== undefined ? className : "")} innerClassName={((this.state.isValid === false || this.state.isMissing === true) ? "field-error" : "")} onChange={this.handleChange} onFocus={this.handleFocus} onBlur={this.handleBlur} value={this.state.value} {...props}>
                        {this.props.children}
                    </ManualSelect>;
                break;
            case "textarea":
                innerElement =
                    <textarea ref={this.innerElement} className={(className !== undefined ? className : "") + ((this.state.isValid === false || this.state.isMissing === true) ? " field-error" : "")} onChange={this.handleChange} onFocus={this.handleFocus} onBlur={this.handleBlur} value={this.state.value} {...props} />;
                break;
            default:
                innerElement =
                    <input ref={this.innerElement} className={(className !== undefined ? className : "") + ((this.state.isValid === false || this.state.isMissing === true) ? " field-error" : "")} onChange={this.handleChange} onFocus={this.handleFocus} onBlur={this.handleBlur} value={this.state.value} {...props} />;
                break;
        }

        return (
            <Fragment>
                {innerElement}
                {this.state.hasFocused ?
                    (this.state.isMissing === true ? <span className="error-text">{requiredMessage || "Field is required"}</span>
                        : this.state.isValid === false
                            ? <span className="error-text">{invalidMessage || "Invalid input"}</span>
                            : "")
                    : ""
                }
            </Fragment>
        );
    }

    private handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
        let value = e.currentTarget.value;

        value = this.runFilter(value);
        e.currentTarget.value = value;
        const element = e.currentTarget;

        this.setState(
            { value, isValid: undefined, isMissing: undefined },
            () => {
                return;
                // HACK: Work-around for cursor-placement issues on kindle fire 7 tablets with kiosk browser
                // eslint-disable-next-line
                if (this.props.type === "text" || this.props.type === "tel" || this.props.type === "email") {
                    window.setTimeout(() => (element as HTMLInputElement).setSelectionRange(value.length, value.length), 10);
                }
            }
        );

        if (this.props.onChange) {
            this.props.onChange(e);
        }
    }

    private handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
        this.runValidation(this.state.value ? this.state.value.toString() : "");

        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    }

    private handleFocus(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
        this.setState({
            hasFocused: true
        });

        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    }

    private runFilter(value: string) {
        if (this.props.filter) {
            if (this.props.filter instanceof RegExp) {
                value = value.replace(this.props.filter, "");
            }
            else if (typeof this.props.filter === "function") {
                value = this.props.filter(value);
            }
        }

        return value;
    }

    private runValidation(value: string, force?: boolean) {
        // value = this.runFilter(value);
        let isValid = true;
        if (this.props.isRequired && value === "") {
            this.setState({ isValid: false, isMissing: true });
            isValid = false;
        }
        else if (this.props.validator && (this.state.hasFocused || force === true)) {
            if (this.props.validator instanceof RegExp && !value.match(this.props.validator)) {
                this.setState({ isValid: false, isMissing: false });
                isValid = false;
            }
            else if (typeof this.props.validator === "function" && !this.props.validator(value)) {
                this.setState({ isValid: false, isMissing: false });
                isValid = false;
            }
            else {
                this.setState({ isValid: true, isMissing: false });
                isValid = true;
            }
        }
        else if (this.props.isRequired && value !== "") {
            this.setState({ isValid: true, isMissing: false });
        }

        this.setState({
            value
        });

        return isValid;
    }
}

export default ValidateInput;
